import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { PatchCoverage, PatchCoverageCategory } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, ErrorBanner, Spinner, StatCard, formatDateTime } from '../common'
import { DataTable, type Column } from '../DataTable'
import { chipClass, platformLabel } from './labels'
import { PercentBar } from './Overview'

export function categoriaLabel(c: PatchCoverageCategory): string {
  switch (c) {
    case 'agent': return t('agente')
    case 'credentials': return t('credenziali')
    case 'stale': return t('lettura vecchia')
    case 'network_only': return t('solo dalla rete')
    default: return t('sistema non riconosciuto')
  }
}

function rimedio(c: PatchCoverageCategory): string {
  switch (c) {
    case 'stale': return t('rifai la scansione con le credenziali o controlla l’agente: senza una lettura recente le patch mancanti non sono aggiornate')
    case 'network_only': return t('installa l’agente CENSO o dai al job una credenziale SSH/WinRM: dalla rete non si vedono le patch')
    case 'unknown_os': return t('il sistema non risulta Windows né Linux: una scansione con credenziali lo riconosce')
    default: return ''
  }
}

/**
 * Coverage (patch_coverage_service): a patch report is only as good as the
 * machines it sees. Which computers CENSO reads from inside (agent, credentials)
 * and which it does not — with the reason and what to do.
 */
export function Coverage() {
  const state = useAsync(() => api.patchCoverage(), [])
  const d: PatchCoverage | null = state.data ?? null
  const colonne: Column<PatchCoverage['machines'][number]>[] = [
    { key: 'name', header: t('Macchina'), value: (r) => r.name, render: (r) => <Link to={`/cis/${r.ci_id}`}>{r.name || r.ci_id.slice(0, 8)}</Link> },
    { key: 'platform', header: t('Sistema'), value: (r) => platformLabel(r.platform), filter: 'select' },
    { key: 'environment', header: t('Ambiente'), value: (r) => r.environment, filter: 'select' },
    { key: 'category', header: t('Lettura'), value: (r) => categoriaLabel(r.category), filter: 'select',
      render: (r) => <span className={chipClass(r.category === 'agent' || r.category === 'credentials' ? 'ok' : 'warn')}>{categoriaLabel(r.category)}</span> },
    { key: 'read', header: t('Letta dall’interno'), type: 'date', value: (r) => r.inside_read_at,
      render: (r) => (r.inside_read_at ? <>{formatDateTime(r.inside_read_at)}{r.inside_source && <span className="text-muted"> · {r.inside_source}</span>}</> : <span className="muted">{t('mai')}</span>) },
    { key: 'missing', header: t('Patch mancanti'), value: (r) => r.missing },
    { key: 'remedy', header: t('Cosa fare'), value: (r) => rimedio(r.category), render: (r) => <span className="text-muted">{rimedio(r.category)}</span> },
  ]
  return (
    <>
      <Card title={t('Copertura')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Un report delle patch vale quanto le macchine che vede: un server letto solo dalla rete non ha patch mancanti perché nessuno ha guardato dentro, non perché è aggiornato. Qui ogni computer del CMDB con la sua lettura: con l’agente, con credenziali negli ultimi {n} giorni, una lettura vecchia, solo dalla rete, sistema non riconosciuto.', { n: d?.recent_days ?? 7 })}
        </p>
        <ErrorBanner error={state.error} />
        {!d ? !state.error && <Spinner /> : (
          <div className="stats">
            <StatCard label={t('Coperte')} value={d.coverage != null ? `${d.coverage} %` : '—'} hint={t('{c} di {n} computer', { c: d.covered, n: d.total })}
                      tone={d.coverage != null && d.coverage < 90 ? 'warn' : 'ok'} />
            <StatCard label={t('Con l’agente')} value={d.by_category.agent} />
            <StatCard label={t('Con credenziali')} value={d.by_category.credentials} />
            <StatCard label={t('Lettura vecchia')} value={d.by_category.stale} tone={d.by_category.stale ? 'warn' : 'ok'} />
            <StatCard label={t('Solo dalla rete')} value={d.by_category.network_only} tone={d.by_category.network_only ? 'warn' : 'ok'} />
            <StatCard label={t('Sistema non riconosciuto')} value={d.by_category.unknown_os} />
          </div>
        )}
        {d && d.by_environment.length > 1 && (
          <table className="table" style={{ marginTop: 12 }}>
            <thead><tr><th>{t('Ambiente')}</th><th>{t('Computer')}</th><th>{t('Coperti')}</th><th /></tr></thead>
            <tbody>
              {d.by_environment.map((g) => (
                <tr key={g.environment}><td>{g.environment === '-' ? t('senza ambiente') : g.environment}</td><td>{g.total}</td><td>{g.covered}</td><td style={{ minWidth: 160 }}><PercentBar n={g.covered} of={g.total} /></td></tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {d && (
        <Card title={t('Computer e lettura')}>
          <DataTable rows={d.machines} columns={colonne} rowKey={(r) => r.ci_id} conteggio={{ nome: t('computer') }} />
        </Card>
      )}
    </>
  )
}
